import type { CalculationSettings } from '../config/calculationSettings';
import { marginalRateFor } from '../config/taxBrackets';
import type { AtoTaxBracket } from '../config/taxBrackets';
import type {
  CashflowResult,
  PropertyType,
  PropertyUse,
  TaxResidency,
} from '../types';

export interface CashflowInput {
  hasPurchase: boolean;
  purchasePrice: number;
  rentalMonthly: number;
  repayMonthly: number;
  propertyUse: PropertyUse;
  propertyType: PropertyType;
  grossAud: number;
  taxResidency: TaxResidency;
  taxBrackets: AtoTaxBracket[];
  settings: CalculationSettings;
}

export function computeCashflow(input: CashflowInput): CashflowResult | null {
  if (!input.hasPurchase) return null;

  const { settings } = input;
  const annualRent = input.rentalMonthly * 12;
  const annualRepay = input.repayMonthly * 12;
  const managementFee =
    input.propertyUse === 'investment'
      ? annualRent * settings.managementFeePct
      : 0;
  const ratesInsurance = input.purchasePrice * settings.ratesInsurancePct;
  const maintenance = input.purchasePrice * settings.maintenancePct;
  const strata =
    input.propertyType === 'unit'
      ? input.purchasePrice * settings.strataUnitPct
      : 0;
  const preTaxCashflow =
    annualRent - annualRepay - managementFee - ratesInsurance - maintenance -
    strata;

  const marginalRate = marginalRateFor(
    input.grossAud,
    input.taxResidency,
    input.taxBrackets,
  );
  const negGearingOffset =
    preTaxCashflow < 0 && input.propertyUse === 'investment'
      ? Math.abs(preTaxCashflow) * marginalRate
      : 0;
  const afterTaxCashflow = preTaxCashflow + negGearingOffset;

  return {
    annualRent,
    annualRepay,
    managementFee,
    ratesInsurance,
    maintenance,
    strata,
    preTaxCashflow,
    marginalRate,
    negGearingOffset,
    afterTaxCashflow,
  };
}
